import React, { useState, useEffect } from 'react';
import { Mail, Plus, Trash2, Users, Download, Search, Check, Clock, X, CheckCircle, Loader2, Send, RotateCcw, ChevronDown, Eye, PauseCircle, PlayCircle, Pencil, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import SubscriberSendModal from './SubscriberSendModal';

// Email cadence map (mirrors backend EMAIL_CADENCE) — used to compute next_send_at in UI
const EMAIL_CADENCE = {
  1:0,2:3,3:4,4:3,5:4,6:3,7:4,8:3,9:4,10:3,11:4,12:3,
  13:7,14:7,15:7,16:7,17:7,18:7,19:7,20:7,21:7,22:7,23:7,24:7,25:7,26:7,27:7,28:7,29:7,30:7,31:7,32:7,
  33:30,34:30,35:30,36:30,37:30,38:30,39:30,40:30,41:30,42:30,43:30,44:30,45:30,46:30,47:30,48:30,49:30,50:30,
};

const computeNextSendAt = (stage, lastSentAt) => {
  const nextStage = stage + 1;
  if (nextStage > 50) return null;
  const days = EMAIL_CADENCE[nextStage];
  if (days === undefined) return null;
  if (days === 0) return new Date();
  if (!lastSentAt) return null;
  return new Date(new Date(lastSentAt).getTime() + days * 86400000);
};

/* ── Change Stage Mini-Modal ── */
const ChangeStageModal = ({ subscriber, onClose, onSuccess }) => {
  const [stage, setStage] = useState(subscriber.nurture_stage ?? 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const { token } = useAuth();

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const csrf = document.cookie.split('; ').find(r => r.startsWith('tcl_csrf='))?.split('=')[1] || '';
      const res = await fetch(
        `${API_URL}/api/cfq/nurture/admin/subscriber/${encodeURIComponent(subscriber.email)}/stage`,
        {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
          credentials: 'include',
          body: JSON.stringify({ stage }),
        }
      );
      if (res.ok) { onSuccess(); onClose(); }
      else { const d = await res.json(); setError(d.detail || 'Failed to update stage'); }
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(4,10,20,0.82)', backdropFilter: 'blur(6px)' }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-navy border border-gold/15 rounded-xl p-5 w-full max-w-xs shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gold/60 mb-0.5">Change Nurture Stage</p>
            <p className="text-cream text-sm font-medium">{subscriber.email}</p>
          </div>
          <button onClick={onClose} className="text-cream/20 hover:text-cream/60"><X size={16} /></button>
        </div>
        <div className="mb-4">
          <label className="block text-[10px] uppercase tracking-wider text-cream/30 mb-2">New stage (0 = restart)</label>
          <div className="flex items-center gap-3">
            <input type="number" min="0" max="50" value={stage} onChange={e => setStage(Math.min(50, Math.max(0, Number(e.target.value))))}
              className="w-24 bg-navy-deep border border-cream/10 rounded-lg px-3 py-2 text-cream text-sm text-center focus:outline-none focus:border-gold/30"
              data-testid="change-stage-input" />
            <span className="text-cream/20 text-xs">of 50</span>
          </div>
          <p className="text-cream/20 text-[10px] mt-1.5">Stage 0 resets enrollment. Next email sends on next cron run.</p>
        </div>
        {error && <p className="text-red-400 text-xs mb-3 flex items-center gap-1"><AlertCircle size={11} />{error}</p>}
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-cream/10 text-cream/40 text-xs hover:border-cream/20 transition-colors">Cancel</button>
          <button onClick={handleSave} disabled={saving} data-testid="save-stage-btn"
            className="flex-1 py-2 rounded-lg bg-gold/15 border border-gold/35 text-gold text-xs font-medium hover:bg-gold/20 disabled:opacity-50 transition-colors">
            {saving ? <Loader2 size={12} className="animate-spin inline" /> : 'Save Stage'}
          </button>
        </div>
      </div>
    </div>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Read the tcl_csrf cookie value (set by the server, readable by JS). */
const getCsrf = () => {
  const m = document.cookie.split('; ').find(r => r.startsWith('tcl_csrf='));
  return m ? m.split('=')[1] : '';
};

/* ── Email 1 Preview Modal ── */
const EmailPreviewModal = ({ onClose }) => (
  <div className="fixed inset-0 z-50 flex flex-col bg-black/80 backdrop-blur-sm" data-testid="email-preview-modal">
    <div className="flex items-center justify-between px-5 py-3 bg-[#06111f] border-b border-gold/[0.08] flex-shrink-0">
      <div className="flex items-center gap-3">
        <div className="w-6 h-6 rounded-md bg-teal-500/10 border border-teal-500/15 flex items-center justify-center">
          <Eye size={11} className="text-teal-400" />
        </div>
        <div>
          <p className="text-cream/70 text-sm font-medium">Email 1 Preview</p>
          <p className="text-cream/20 text-[10px]">Cultural Fluency Assessment — what inactive subscribers will receive</p>
        </div>
      </div>
      <button onClick={onClose} data-testid="close-email-preview-btn" className="w-7 h-7 flex items-center justify-center rounded-lg text-cream/30 hover:text-cream/80 hover:bg-cream/[0.05] transition-colors">
        <X size={15} />
      </button>
    </div>
    <div className="flex-1 bg-[#f0ede6] overflow-hidden">
      <iframe src={`${API_URL}/api/cfq/nurture/preview/1`} title="Email 1 Preview" className="w-full h-full border-0" data-testid="email-preview-iframe" />
    </div>
  </div>
);

/* ── Add Subscriber Modal ── */
const AddSubscriberModal = ({ form, onChange, onSubmit, onClose }) => (
  <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
    <div className="bg-navy border border-gold/10 rounded-xl max-w-md w-full p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-cream text-lg font-semibold">Add Subscriber</h2>
        <button onClick={onClose} className="text-cream/30 hover:text-cream"><X size={18} /></button>
      </div>
      <p className="text-cream/30 text-xs mb-4">Added directly by admin — confirmed immediately, no email verification required.</p>
      <form onSubmit={onSubmit} className="space-y-3">
        <input value={form.email} onChange={e => onChange('email', e.target.value)} placeholder="Email address" type="email" required className="w-full bg-navy-deep border border-cream/10 rounded-lg px-4 py-2.5 text-cream text-sm placeholder-cream/20 focus:outline-none focus:border-gold/30" data-testid="add-email-input" />
        <input value={form.first_name} onChange={e => onChange('first_name', e.target.value)} placeholder="First name (optional)" className="w-full bg-navy-deep border border-cream/10 rounded-lg px-4 py-2.5 text-cream text-sm placeholder-cream/20 focus:outline-none focus:border-gold/30" data-testid="add-name-input" />
        <button type="submit" className="w-full bg-gold text-navy py-2.5 rounded-lg text-sm font-medium hover:bg-gold/90" data-testid="add-submit-btn">Add subscriber</button>
      </form>
    </div>
  </div>
);

/* ── Re-engagement Panel ── */
const ReEngagementPanel = ({ inactiveStats, loading, result, onStart, onClearResult }) => {
  const [open, setOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  return (
    <>
      <div className="mb-6 bg-navy border border-gold/[0.06] rounded-xl overflow-hidden" data-testid="re-engagement-panel">
        <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-cream/[0.01] transition-colors" data-testid="re-engage-toggle">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-teal-500/10 border border-teal-500/15 flex items-center justify-center flex-shrink-0">
              <RotateCcw size={13} className="text-teal-400" />
            </div>
            <div className="text-left">
              <p className="text-cream/60 text-xs font-medium">Re-engagement Campaign</p>
              <p className="text-cream/25 text-[10px] mt-0.5">{inactiveStats.total_eligible} subscriber{inactiveStats.total_eligible !== 1 ? 's' : ''} haven't received an email in 90+ days</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-teal-500/10 text-teal-400 border border-teal-500/15">{inactiveStats.total_eligible} eligible</span>
            {open ? <ChevronDown size={13} className="text-cream/20" /> : <ChevronDown size={13} className="text-cream/20 -rotate-90" />}
          </div>
        </button>

        {open && (
          <div className="border-t border-gold/[0.04] px-5 py-4">
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-navy-deep border border-gold/[0.04] rounded-xl p-3">
                <p className="text-cream/20 text-[10px] uppercase tracking-wider">Never received email</p>
                <p className="text-cream text-xl font-light mt-0.5">{inactiveStats.never_sent_count}</p>
              </div>
              <div className="bg-navy-deep border border-gold/[0.04] rounded-xl p-3">
                <p className="text-cream/20 text-[10px] uppercase tracking-wider">Inactive 90+ days</p>
                <p className="text-cream text-xl font-light mt-0.5">{inactiveStats.inactive_count}</p>
              </div>
            </div>

            {inactiveStats.sample?.length > 0 && (
              <div className="mb-4">
                <p className="text-cream/25 text-[10px] uppercase tracking-wider mb-2">Sample subscribers</p>
                <div className="space-y-1">
                  {inactiveStats.sample.map(s => (
                    <div key={s.email} className="flex items-center justify-between text-xs">
                      <span className="text-cream/40 font-mono">{s.email}</span>
                      <span className="text-cream/20">stage {s.nurture_stage ?? 0}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4 p-3 bg-cream/[0.02] rounded-xl border border-gold/[0.04]">
              <div className="flex items-start justify-between gap-3">
                <p className="text-cream/40 text-xs leading-relaxed">
                  Clicking <strong className="text-cream/60">Start Campaign</strong> will reset all {inactiveStats.total_eligible} subscribers to stage 0. They will receive <strong className="text-cream/60">Email 1</strong> — the Cultural Fluency Assessment introduction — on the next morning's cron run (daily at 07:00 UTC).
                </p>
                <button onClick={() => setShowPreview(true)} data-testid="preview-email1-btn" className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-gold/[0.07] border border-gold/[0.12] text-gold/60 hover:text-gold hover:bg-gold/[0.12] text-[10px] font-medium transition-all">
                  <Eye size={11} />
                  Preview Email 1
                </button>
              </div>
            </div>

            {result && (
              <div className="mb-3 p-3 rounded-xl text-xs border bg-teal-500/10 border-teal-500/20 text-teal-300 flex items-center justify-between" data-testid="re-engage-success">
                <span>{result.count} subscriber{result.count !== 1 ? 's' : ''} queued for Email 1 — they will receive it on the next cron run.</span>
                <button onClick={onClearResult} className="ml-2 opacity-50 hover:opacity-100"><X size={10} /></button>
              </div>
            )}

            <button onClick={onStart} disabled={loading || result !== null} data-testid="re-engage-btn" className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-teal-500/10 border border-teal-500/20 text-teal-300 hover:bg-teal-500/15 text-xs transition-all disabled:opacity-50">
              {loading
                ? <><Loader2 size={13} className="animate-spin" /> Starting campaign…</>
                : result
                ? <><Check size={13} /> Campaign started — {result.count} queued</>
                : <><RotateCcw size={13} /> Start Re-engagement Campaign ({inactiveStats.total_eligible} subscribers)</>}
            </button>
          </div>
        )}
      </div>
      {showPreview && <EmailPreviewModal onClose={() => setShowPreview(false)} />}
    </>
  );
};

const AdminMailing = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', first_name: '' });
  const [stats, setStats] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [confirmingAll, setConfirmingAll] = useState(false);
  const [remindingAll, setRemindingAll] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [sendModal, setSendModal] = useState(null);
  // Bulk selection state
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [bulkResetLoading, setBulkResetLoading] = useState(false);
  const [bulkResetResult, setBulkResetResult] = useState(null);
  // Re-engagement state
  const [inactiveStats, setInactiveStats] = useState(null);
  const [reEngageLoading, setReEngageLoading] = useState(false);
  const [reEngageResult, setReEngageResult] = useState(null);
  // Pause / Change-stage state
  const [pauseLoading, setPauseLoading] = useState(null); // email string while loading
  const [changeStageModal, setChangeStageModal] = useState(null); // subscriber object

  const { token } = useAuth();
  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const mutHeaders  = { ...authHeaders, 'X-CSRF-Token': getCsrf() };

  const fetchSubscribers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), page_size: '30' });
      if (search) params.set('search', search);
      const res = await fetch(`${API_URL}/api/cfq/admin/mailing-list?${params}`, {
        headers: authHeaders, credentials: 'include',
      });
      if (res.ok) {
        const d = await res.json();
        setSubscribers(d.subscribers || []);
        setTotal(d.total || 0);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cfq/admin/stats`, {
        headers: authHeaders, credentials: 'include',
      });
      if (res.ok) setStats(await res.json());
    } catch {}
  };

  useEffect(() => { fetchSubscribers(); fetchStats(); }, [page]); // eslint-disable-line
  useEffect(() => { setPage(1); fetchSubscribers(); }, [search]); // eslint-disable-line

  // Fetch inactive stats for re-engagement panel
  useEffect(() => {
    fetch(`${API_URL}/api/cfq/nurture/admin/inactive-stats`, {
      headers: authHeaders, credentials: 'include',
    }).then(r => r.ok ? r.json() : null).then(d => { if (d) setInactiveStats(d); }).catch(() => {});
  }, []); // eslint-disable-line

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addForm.email) return;
    try {
      const res = await fetch(`${API_URL}/api/cfq/admin/add-subscriber`, {
        method: 'POST',
        headers: mutHeaders,
        credentials: 'include',
        body: JSON.stringify(addForm),
      });
      if (res.ok) {
        setShowAdd(false);
        setAddForm({ email: '', first_name: '' });
        fetchSubscribers();
        fetchStats();
      }
    } catch {}
  };

  const handleConfirm = async (email) => {
    setActionLoading(email + '-confirm');
    await fetch(`${API_URL}/api/cfq/admin/confirm/${encodeURIComponent(email)}`, {
      method: 'PATCH', headers: mutHeaders, credentials: 'include',
    });
    setActionLoading(null);
    fetchSubscribers(); fetchStats();
  };

  const handleResend = async (email) => {
    setActionLoading(email + '-resend');
    try {
      const res = await fetch(`${API_URL}/api/cfq/admin/resend-confirmation/${encodeURIComponent(email)}`, {
        method: 'POST', headers: mutHeaders, credentials: 'include',
      });
      const d = await res.json();
      alert(res.ok ? `Reminder sent to ${email}` : (d.detail || 'Failed to send reminder'));
    } catch { alert('Failed to send reminder'); }
    setActionLoading(null);
  };

  const handleRemove = async (email) => {
    if (!window.confirm(`Remove ${email} from the mailing list?`)) return;
    setActionLoading(email + '-remove');
    await fetch(`${API_URL}/api/cfq/admin/mailing/${encodeURIComponent(email)}`, {
      method: 'DELETE', headers: mutHeaders, credentials: 'include',
    });
    setActionLoading(null);
    fetchSubscribers(); fetchStats();
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_URL}/api/cfq/admin/mailing-export`, {
        headers: authHeaders, credentials: 'include',
      });
      if (res.ok) {
        const blob = await res.blob();
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href = url;
        a.download = `mailing-list-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) { console.error(e); }
    setExporting(false);
  };

  const handleConfirmAll = async () => {
    const pending = stats?.mailing_pending ?? 0;
    if (pending === 0) return;
    if (!window.confirm(`Confirm all ${pending} pending subscriber${pending === 1 ? '' : 's'} without email verification?`)) return;
    setConfirmingAll(true);
    try {
      const res = await fetch(`${API_URL}/api/cfq/admin/confirm-all-pending`, {
        method: 'PATCH', headers: mutHeaders, credentials: 'include',
      });
      if (res.ok) {
        const d = await res.json();
        fetchSubscribers();
        fetchStats();
        alert(`${d.confirmed_count} subscriber${d.confirmed_count === 1 ? '' : 's'} confirmed.`);
      }
    } catch (e) { console.error(e); }
    setConfirmingAll(false);
  };

  const handleReEngage = async () => {
    if (!window.confirm(`This will reset ${inactiveStats?.total_eligible || 0} inactive subscriber(s) to stage 0. They will receive Email 1 on the next morning's cron run. Continue?`)) return;
    setReEngageLoading(true);
    setReEngageResult(null);
    try {
      const res = await fetch(`${API_URL}/api/cfq/nurture/admin/bulk-reset-stage`, {
        method: 'POST',
        headers: mutHeaders,
        credentials: 'include',
        body: JSON.stringify({ filter: 'all_inactive' }),
      });
      const d = await res.json();
      if (res.ok) {
        setReEngageResult({ count: d.reset_count });
        // Refresh stats
        fetch(`${API_URL}/api/cfq/nurture/admin/inactive-stats`, {
          headers: authHeaders, credentials: 'include',
        }).then(r => r.ok ? r.json() : null).then(d => { if (d) setInactiveStats(d); }).catch(() => {});
        fetchStats();
      }
    } catch (e) { console.error(e); }
    setReEngageLoading(false);
  };

  const handleBulkReset = async () => {
    const emails = [...selectedEmails];
    if (emails.length === 0) return;
    if (!window.confirm(`Reset nurture stage to 0 for ${emails.length} subscriber(s)? They will re-receive Email 1 on the next cron run.`)) return;
    setBulkResetLoading(true);
    setBulkResetResult(null);
    try {
      const res = await fetch(`${API_URL}/api/cfq/nurture/admin/bulk-reset-stage`, {
        method: 'POST',
        headers: mutHeaders,
        credentials: 'include',
        body: JSON.stringify({ emails }),
      });
      const d = await res.json();
      if (res.ok) {
        setBulkResetResult({ count: d.reset_count });
        setSelectedEmails(new Set());
        fetchSubscribers();
        fetchStats();
      }
    } catch (e) { console.error(e); }
    setBulkResetLoading(false);
  };

  const toggleSelectAll = () => {
    if (selectedEmails.size === subscribers.length) {
      setSelectedEmails(new Set());
    } else {
      setSelectedEmails(new Set(subscribers.map(s => s.email)));
    }
  };

  const toggleSelect = (email) => {
    setSelectedEmails(prev => {
      const next = new Set(prev);
      next.has(email) ? next.delete(email) : next.add(email);
      return next;
    });
  };

  const handleRemindAll = async () => {    const pending = stats?.mailing_pending ?? 0;
    if (pending === 0) { alert('No pending subscribers to remind.'); return; }
    if (!window.confirm(`Send a confirmation reminder email to all ${pending} unconfirmed subscriber${pending === 1 ? '' : 's'}?`)) return;
    setRemindingAll(true);
    try {
      const res = await fetch(`${API_URL}/api/cfq/admin/resend-all-pending`, {
        method: 'POST', headers: mutHeaders, credentials: 'include',
      });
      const d = await res.json();
      if (res.ok) {
        alert(`Reminder sent to ${d.sent} subscriber${d.sent === 1 ? '' : 's'}${d.failed > 0 ? ` (${d.failed} failed)` : ''}.`);
      } else {
        alert(d.detail || 'Failed to send reminders.');
      }
    } catch (e) { alert('Failed to send reminders.'); }
    setRemindingAll(false);
  };

  const totalPages = Math.ceil(total / 30);

  const handlePauseResume = async (subscriber) => {
    const { email, nurture_paused } = subscriber;
    const action = nurture_paused ? 'resume' : 'pause';
    setPauseLoading(email);
    try {
      const res = await fetch(
        `${API_URL}/api/cfq/nurture/admin/subscriber/${encodeURIComponent(email)}/${action}`,
        { method: 'PATCH', headers: mutHeaders, credentials: 'include' }
      );
      if (res.ok) fetchSubscribers();
    } catch {}
    setPauseLoading(null);
  };

  return (
    <>
    <div data-testid="admin-mailing">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-2xl text-cream font-semibold">Mailing List</h1>
          <p className="text-cream/30 text-sm mt-1">
            {stats
              ? `${stats.mailing_confirmed ?? 0} confirmed · ${stats.mailing_pending ?? 0} pending · ${total} total`
              : 'Loading…'}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-cream/10 text-cream/50 text-xs rounded-lg hover:border-gold/20 hover:text-gold disabled:opacity-40 transition-colors"
            data-testid="export-mailing-btn"
          >
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Export CSV
          </button>
          {(stats?.mailing_pending ?? 0) > 0 && (
            <button
              onClick={handleConfirmAll}
              disabled={confirmingAll}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-amber-500/30 text-amber-400/70 text-xs rounded-lg hover:border-amber-400/50 hover:text-amber-300 disabled:opacity-40 transition-colors"
              data-testid="confirm-all-pending-btn"
            >
              {confirmingAll ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
              Confirm all pending ({stats.mailing_pending})
            </button>
          )}
          <button
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-navy text-sm font-medium rounded-lg hover:bg-gold/90 transition-colors"
            data-testid="add-subscriber-btn"
          >
            <Plus size={16} /> Add subscriber
          </button>
        </div>
      </div>

      {/* Re-engagement Panel */}
      {inactiveStats && inactiveStats.total_eligible > 0 && (
        <ReEngagementPanel
          inactiveStats={inactiveStats}
          loading={reEngageLoading}
          result={reEngageResult}
          onStart={handleReEngage}
          onClearResult={() => setReEngageResult(null)}
        />
      )}

      {/* Add Subscriber Modal */}
      {showAdd && (
        <AddSubscriberModal
          form={addForm}
          onChange={(field, val) => setAddForm(prev => ({ ...prev, [field]: val }))}
          onSubmit={handleAdd}
          onClose={() => setShowAdd(false)}
        />
      )}

      {/* Bulk reset success banner */}
      {bulkResetResult && (
        <div className="mb-4 p-3 rounded-xl text-xs border bg-teal-500/10 border-teal-500/20 text-teal-300 flex items-center justify-between" data-testid="bulk-reset-success">
          <span>{bulkResetResult.count} subscriber(s) reset to stage 0 — they will receive Email 1 on the next cron run.</span>
          <button onClick={() => setBulkResetResult(null)} className="ml-2 opacity-50 hover:opacity-100"><X size={10} /></button>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-cream/20" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by email or name…"
          className="w-full bg-navy border border-cream/10 rounded-lg pl-10 pr-4 py-2.5 text-cream text-sm placeholder-cream/20 focus:outline-none focus:border-gold/30"
          data-testid="search-subscribers-input"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="py-12 flex items-center justify-center text-cream/20 gap-2">
          <Loader2 size={18} className="animate-spin" />
          <span className="text-sm">Loading…</span>
        </div>
      ) : subscribers.length === 0 ? (
        <div className="py-16 text-center bg-navy border border-dashed border-gold/[0.06] rounded-xl">
          <Users size={28} className="mx-auto text-cream/10 mb-2" />
          <p className="text-cream/25 text-sm">{search ? 'No matching subscribers' : 'No subscribers yet'}</p>
        </div>
      ) : (
        <div className="bg-navy border border-gold/[0.06] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gold/[0.04]">
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={selectedEmails.size === subscribers.length && subscribers.length > 0}
                    onChange={toggleSelectAll}
                    data-testid="select-all-checkbox"
                    className="accent-gold cursor-pointer"
                  />
                </th>
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20">Email</th>
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20 hidden md:table-cell">Name</th>
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20">Status</th>
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20 hidden md:table-cell">Stage</th>
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20 hidden lg:table-cell">Next email</th>
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20 hidden lg:table-cell">Source</th>
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20 hidden sm:table-cell">Joined</th>
                <th className="text-right px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {subscribers.map((s) => {
                  const srcLabel = { homepage: 'Homepage', article: 'Article', footer: 'Footer' }[s.source] || s.source || '—';
                  const srcColor = { homepage: 'text-blue-300/60', article: 'text-gold/60', footer: 'text-emerald-400/60' }[s.source] || 'text-cream/15';
                  const isSelected = selectedEmails.has(s.email);
                  const isPaused = s.nurture_paused === true;
                  const nextSendAt = computeNextSendAt(s.nurture_stage ?? 0, s.nurture_last_sent_at);
                  const nextSendDays = nextSendAt ? Math.ceil((nextSendAt - Date.now()) / 86400000) : null;
                  return (
                <tr key={s.email} className={`border-b border-gold/[0.03] hover:bg-cream/[0.01] transition-colors ${isSelected ? 'bg-gold/[0.03]' : ''} ${isPaused ? 'opacity-60' : ''}`} data-testid={`subscriber-row-${s.email}`}>
                  <td className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(s.email)}
                      data-testid={`select-${s.email}`}
                      className="accent-gold cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3 text-cream/60 text-sm font-mono">
                    <div className="flex items-center gap-1.5">
                      {isPaused && <PauseCircle size={11} className="text-amber-400/60 flex-shrink-0" title="Nurture paused" />}
                      {s.email}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-cream/30 text-sm hidden md:table-cell">{s.first_name || '—'}</td>
                  <td className="px-4 py-3">
                    {s.confirmed ? (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400">
                        <CheckCircle size={10} /> Confirmed
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400">
                        <Clock size={10} /> Pending
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell" data-testid={`stage-${s.email}`}>
                    {s.nurture_stage > 0 ? (
                      <div>
                        <div className="flex items-center gap-1.5">
                          <div className="text-[11px] tabular-nums leading-none" style={{ color: '#C99316' }}>
                            {s.nurture_stage}<span style={{ color: 'rgba(250,248,243,0.2)', fontWeight: 400 }}>/50</span>
                          </div>
                          {isPaused && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400/70">paused</span>}
                        </div>
                        <div className="mt-1.5 h-[3px] rounded-full overflow-hidden" style={{ width: '48px', background: 'rgba(250,248,243,0.06)' }}>
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${(s.nurture_stage / 50) * 100}%`, background: 'linear-gradient(90deg,#8a6510,#C99316)' }}
                          />
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs" style={{ color: 'rgba(250,248,243,0.15)' }}>—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell" data-testid={`next-send-${s.email}`}>
                    {s.nurture_stage >= 50 ? (
                      <span className="text-[10px] text-gold/40">Complete</span>
                    ) : isPaused ? (
                      <span className="text-[10px] text-amber-400/50">Paused</span>
                    ) : nextSendAt ? (
                      <div>
                        <span className="text-[11px] tabular-nums" style={{ color: 'rgba(250,248,243,0.35)' }}>
                          Email {(s.nurture_stage ?? 0) + 1}
                        </span>
                        <span className="text-[10px] ml-1.5" style={{ color: nextSendDays !== null && nextSendDays <= 0 ? 'rgba(74,222,128,0.7)' : 'rgba(250,248,243,0.2)' }}>
                          {nextSendDays !== null && nextSendDays <= 0 ? 'due today' : nextSendDays === 1 ? 'in 1d' : nextSendDays !== null ? `in ${nextSendDays}d` : ''}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs" style={{ color: 'rgba(250,248,243,0.12)' }}>—</span>
                    )}
                  </td>
                  <td className={`px-4 py-3 text-xs hidden lg:table-cell ${srcColor}`} data-testid={`source-${s.email}`}>{srcLabel}</td>
                  <td className="px-4 py-3 text-cream/15 text-xs hidden sm:table-cell tabular-nums">
                    {s.created_at ? new Date(s.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {s.confirmed && (
                        <button
                          onClick={() => setSendModal(s)}
                          className="p-1.5 text-gold/30 hover:text-gold hover:bg-gold/10 rounded transition-colors"
                          title="Send a nurture email"
                          data-testid={`send-email-${s.email}`}
                        >
                          <Mail size={13} />
                        </button>
                      )}
                      {s.confirmed && (
                        <button
                          onClick={() => setChangeStageModal(s)}
                          className="p-1.5 text-blue-400/30 hover:text-blue-400 hover:bg-blue-500/10 rounded transition-colors"
                          title="Change nurture stage"
                          data-testid={`change-stage-${s.email}`}
                        >
                          <Pencil size={13} />
                        </button>
                      )}
                      {s.confirmed && (
                        <button
                          onClick={() => handlePauseResume(s)}
                          disabled={pauseLoading === s.email}
                          className="p-1.5 text-amber-400/30 hover:text-amber-400 hover:bg-amber-500/10 rounded transition-colors disabled:opacity-30"
                          title={isPaused ? 'Resume nurture' : 'Pause nurture'}
                          data-testid={`pause-resume-${s.email}`}
                        >
                          {pauseLoading === s.email
                            ? <Loader2 size={13} className="animate-spin" />
                            : isPaused ? <PlayCircle size={13} /> : <PauseCircle size={13} />}
                        </button>
                      )}
                      {!s.confirmed && (
                        <button
                          onClick={() => handleConfirm(s.email)}
                          disabled={actionLoading === s.email + '-confirm'}
                          className="p-1.5 text-emerald-400/40 hover:text-emerald-400 hover:bg-emerald-500/10 rounded transition-colors disabled:opacity-30"
                          title="Confirm manually"
                          data-testid={`confirm-${s.email}`}
                        >
                          {actionLoading === s.email + '-confirm'
                            ? <Loader2 size={13} className="animate-spin" />
                            : <Check size={14} />}
                        </button>
                      )}
                      {!s.confirmed && (
                        <button
                          onClick={() => handleResend(s.email)}
                          disabled={actionLoading === s.email + '-resend'}
                          className="p-1.5 text-amber-400/40 hover:text-amber-400 hover:bg-amber-500/10 rounded transition-colors disabled:opacity-30"
                          title="Send confirmation reminder"
                          data-testid={`resend-${s.email}`}
                        >
                          {actionLoading === s.email + '-resend'
                            ? <Loader2 size={13} className="animate-spin" />
                            : <Send size={13} />}
                        </button>
                      )}
                      <button
                        onClick={() => handleRemove(s.email)}
                        disabled={actionLoading === s.email + '-remove'}
                        className="p-1.5 text-cream/15 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors disabled:opacity-30"
                        title="Remove subscriber"
                        data-testid={`remove-${s.email}`}
                      >
                        {actionLoading === s.email + '-remove'
                          ? <Loader2 size={13} className="animate-spin" />
                          : <Trash2 size={14} />}
                      </button>
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gold/[0.04]">
              <p className="text-cream/15 text-xs tabular-nums">{total} subscribers · Page {page} of {totalPages}</p>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 text-cream/30 text-xs border border-cream/10 rounded hover:text-gold disabled:opacity-30 transition-colors"
                  data-testid="prev-page-btn"
                >Prev</button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 text-cream/30 text-xs border border-cream/10 rounded hover:text-gold disabled:opacity-30 transition-colors"
                  data-testid="next-page-btn"
                >Next</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>

    {/* Bulk action bar — fixed at bottom when items are selected */}
    {selectedEmails.size > 0 && (
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-[#06111f] border border-gold/20 rounded-2xl px-5 py-3 shadow-2xl" data-testid="bulk-action-bar">
        <span className="text-cream/50 text-xs">{selectedEmails.size} selected</span>
        <div className="w-px h-4 bg-gold/20" />
        <button
          onClick={handleBulkReset}
          disabled={bulkResetLoading}
          data-testid="bulk-reset-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-500/10 border border-teal-500/20 text-teal-300 hover:bg-teal-500/15 text-xs transition-all disabled:opacity-50"
        >
          {bulkResetLoading
            ? <Loader2 size={12} className="animate-spin" />
            : <RotateCcw size={12} />}
          Reset to Stage 0
        </button>
        <button
          onClick={() => setSelectedEmails(new Set())}
          className="p-1.5 text-cream/20 hover:text-cream/50 transition-colors"
          data-testid="bulk-clear-btn"
        >
          <X size={14} />
        </button>
      </div>
    )}

    {sendModal && (
      <SubscriberSendModal
        subscriber={sendModal}
        onClose={() => setSendModal(null)}
        onSuccess={() => { fetchSubscribers(); }}
      />
    )}

    {changeStageModal && (
      <ChangeStageModal
        subscriber={changeStageModal}
        onClose={() => setChangeStageModal(null)}
        onSuccess={() => { setChangeStageModal(null); fetchSubscribers(); fetchStats(); }}
      />
    )}
    </>
  );
};


export default AdminMailing;
